import React, { useEffect, useState, useRef } from "react";
import useCardStore from "./store/shoppingCardStore";
import styles from "./Login.module.css";
import { useNavigate } from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { LocateFixed } from "lucide-react";

// تنظیم آیکون مارکر
const DefaultIcon = L.icon({
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

const Register = () => {
  const navigate = useNavigate();
  const [userLocation, setUserLocation] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  // دریافت موقعیت کاربر
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({
            lat: latitude,
            lng: longitude,
          });
          if (mapRef.current) {
            mapRef.current.flyTo([latitude, longitude], 15);
          }
        },
        (error) => {
          console.error("Error getting location:", error);
          // موقعیت پیش‌فرض تهران
          const defaultLocation = { lat: 35.6892, lng: 51.389 };
          setUserLocation(defaultLocation);
          if (mapRef.current) {
            mapRef.current.flyTo(
              [defaultLocation.lat, defaultLocation.lng],
              12
            );
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0,
        }
      );
    } else {
      // اگر مرورگر از geolocation پشتیبانی نمی‌کند
      const defaultLocation = { lat: 35.6892, lng: 51.389 };
      setUserLocation(defaultLocation);
    }
  }, []);

  // آپدیت موقعیت مارکر
  const updateMarkerPosition = (lat, lng) => {
    if (markerRef.current) {
      markerRef.current.setLatLng([lat, lng]);
    }
    setUserLocation({ lat, lng });
  };

  // هندل کلیک روی نقشه
  const handleMapClick = (e) => {
    updateMarkerPosition(e.latlng.lat, e.latlng.lng);
  };

  // اطلاعات سبد خرید
  const { items, itemscount, resetItems } = useCardStore();

  // محاسبه مجموع پرداخت
  const totalPayment = items.reduce(
    (total, item) => total + item.price * (itemscount[item.id] || 1),
    0
  );

  // اطلاعات فرم
  const [formData, setFormData] = useState({
    fullname: "",
    phone: "",
    address: "",
    postalCode: "",
    additionalInfo: "",
    acceptTerms: false,
  });

  const [touched, setTouched] = useState({
    fullname: false,
    phone: false,
    address: false,
    acceptTerms: false,
  });

  // اعتبارسنجی فرم
  const validate = () => {
    const errors = {};

    if (!formData.fullname.trim()) {
      errors.fullname = "نام و نام خانوادگی الزامی است";
    } else if (formData.fullname.trim().length < 3) {
      errors.fullname = "نام و نام خانوادگی باید حداقل 3 کاراکتر باشد";
    }

    if (!formData.phone.trim()) {
      errors.phone = "شماره همراه الزامی است";
    } else if (!/^09\d{9}$/.test(formData.phone)) {
      errors.phone = "شماره همراه معتبر نیست (مثال: 09123456789)";
    }

    if (!formData.address.trim()) {
      errors.address = "آدرس الزامی است";
    } else if (formData.address.trim().length < 10) {
      errors.address = "آدرس باید حداقل 10 کاراکتر باشد";
    }

    if (!formData.acceptTerms) {
      errors.acceptTerms = "پذیرش قوانین الزامی است";
    }

    return errors;
  };

  const [errors, setErrors] = useState({});

  // تغییر فیلدهای فرم
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Validate on change if field has been touched
    if (touched[name]) {
      setErrors(validate());
    }
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    setErrors(validate());
  };

  // ارسال فرم
  const handleSubmit = async (e) => {
    e.preventDefault();

    const formErrors = validate();
    setErrors(formErrors);
    setTouched({
      fullname: true,
      phone: true,
      address: true,
      acceptTerms: true,
    });

    if (Object.keys(formErrors).length > 0) {
      return;
    }

    if (!userLocation) {
      setSubmitError("لطفا موقعیت خود را روی نقشه مشخص کنید");
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const orderData = {
        customer: {
          fullname: formData.fullname,
          phone: formData.phone,
          address: formData.address,
          ...(formData.postalCode && { postalCode: formData.postalCode }),
          ...(formData.additionalInfo && {
            additionalInfo: formData.additionalInfo,
          }),
          location: {
            lat: userLocation.lat,
            lng: userLocation.lng,
          },
        },
        items: items.map((item) => ({
          productId: item.id,
          name: item.name,
          quantity: itemscount[item.id] || 1,
          price: item.price,
          image: item.image[0],
        })),
        totalAmount: totalPayment,
      };

      const response = await fetch("https://backend.battery100.ir/api/orders", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(orderData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "خطا در ثبت سفارش");
      }

      const result = await response.json();
      setSubmitSuccess(true);
      resetItems();

      setTimeout(() => {
        navigate("/چاپ سفارش/", {
          state: {
            orderId: result.orderId,
            customerName: formData.fullname,
            totalAmount: totalPayment,
          },
        });
      }, 500);
    } catch (error) {
      console.error("Submission error:", error);
      setSubmitError(
        error.message || "خطا در ارسال سفارش. لطفا دوباره تلاش کنید."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLocateMe = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newPos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          updateMarkerPosition(newPos.lat, newPos.lng);
          if (mapRef.current) {
            mapRef.current.flyTo([newPos.lat, newPos.lng], 15);
          }
        },
        (error) => {
          console.error("Error getting location:", error);
          setSubmitError(
            "خطا در دریافت موقعیت جغرافیایی. لطفا به صورت دستی روی نقشه کلیک کنید."
          );
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
        }
      );
    } else {
      setSubmitError(
        "مرورگر شما از ویژگی موقعیت جغرافیایی پشتیبانی نمی‌کند. لطفا به صورت دستی روی نقشه کلیک کنید."
      );
    }
  };

  if (items.length === 0) {
    return (
      <div className={styles.emptyCart}>
        <h2>سبد خرید شما خالی است</h2>
        <button onClick={() => navigate("/")}>بازگشت به فروشگاه</button>
      </div>
    );
  }

  return (
    <>
      <Navbar />

      <div className={styles.container}>
        <h1 className={styles.title}>ثبت سفارش</h1>

        {/* آیتم‌های سفارش */}
        <div className={styles.orderItems}>
          {items.map((item) => (
            <div key={item.id} className={styles.orderItem}>
              <img
                src={item.image[0]}
                alt={item.name}
                className={styles.itemImage}
              />
              <div className={styles.itemDetails}>
                <h3>{item.name}</h3>
                <p>تعداد: {itemscount[item.id] || 1}</p>
                <p>قیمت: {item.price.toLocaleString()} تومان</p>
              </div>
            </div>
          ))}
        </div>

        {/* مجموع پرداخت */}
        <div className={styles.orderTotal}>
          <h2>مجموع پرداخت:</h2>
          <p>{totalPayment.toLocaleString()} تومان</p>
        </div>

        {/* فرم اطلاعات مشتری */}
        <h1 className={styles.formTitle}>ثبت اطلاعات تحویل گیرنده</h1>

        <form onSubmit={handleSubmit} className={styles.orderForm}>
          {/* نام کامل */}
          <div className={styles.formGroup}>
            <label>نام و نام خانوادگی *</label>
            <input
              type="text"
              name="fullname"
              value={formData.fullname}
              onChange={handleChange}
              onBlur={() => handleBlur("fullname")}
              className={
                touched.fullname && errors.fullname ? styles.errorInput : ""
              }
              placeholder="نام و نام خانوادگی خود را وارد کنید"
            />
            {touched.fullname && errors.fullname && (
              <span className={styles.errorText}>{errors.fullname}</span>
            )}
          </div>

          {/* تلفن همراه */}
          <div className={styles.formGroup}>
            <label>شماره همراه *</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              onBlur={() => handleBlur("phone")}
              className={touched.phone && errors.phone ? styles.errorInput : ""}
              placeholder="مثال: 09123456789"
            />
            {touched.phone && errors.phone && (
              <span className={styles.errorText}>{errors.phone}</span>
            )}
          </div>

          {/* آدرس */}
          <div className={styles.formGroup}>
            <label>آدرس *</label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleChange}
              onBlur={() => handleBlur("address")}
              rows={3}
              className={
                touched.address && errors.address ? styles.errorInput : ""
              }
              placeholder="آدرس کامل شامل خیابان، کوچه، پلاک و ..."
            />
            {touched.address && errors.address && (
              <span className={styles.errorText}>{errors.address}</span>
            )}
          </div>

          {/* نقشه */}
          <div className={styles.mapContainer}>
            <div className={styles.mapHeader}>
              <h3>موقعیت روی نقشه (برای تغییر موقعیت روی نقشه کلیک کنید)</h3>
              <button
                type="button"
                onClick={handleLocateMe}
                className={styles.locateButton}
                title="موقعیت فعلی من"
                aria-label="موقعیت فعلی من"
              >
                <LocateFixed size={20} />
              </button>
            </div>
            {userLocation ? (
              <MapContainer
                center={[userLocation.lat, userLocation.lng]}
                zoom={15}
                className={styles.map}
                whenCreated={(map) => {
                  mapRef.current = map;
                  map.on("click", handleMapClick);
                }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                <Marker
                  position={[userLocation.lat, userLocation.lng]}
                  ref={markerRef}
                  draggable={true}
                  eventHandlers={{
                    dragend: (e) => {
                      const marker = e.target;
                      const position = marker.getLatLng();
                      updateMarkerPosition(position.lat, position.lng);
                    },
                  }}
                >
                  <Popup>موقعیت تحویل سفارش</Popup>
                </Marker>
              </MapContainer>
            ) : (
              <div className={styles.mapLoading}>
                <p>در حال دریافت موقعیت...</p>
              </div>
            )}
          </div>

          {/* کد پستی */}
          <div className={styles.formGroup}>
            <label>کد پستی (اختیاری)</label>
            <input
              type="text"
              name="postalCode"
              value={formData.postalCode}
              onChange={handleChange}
              placeholder="مثال: 1234567890"
            />
          </div>

          {/* توضیحات اضافی */}
          <div className={styles.formGroup}>
            <label>توضیحات (اختیاری)</label>
            <textarea
              name="additionalInfo"
              value={formData.additionalInfo}
              onChange={handleChange}
              rows={3}
              placeholder="توضیحات اضافی مانند شماره واحد، کد درب و ..."
            />
          </div>

          {/* قوانین */}
          <div className={`${styles.formGroup} ${styles.termsGroup}`}>
            <label className={styles.termsLabel}>
              <input
                type="checkbox"
                name="acceptTerms"
                checked={formData.acceptTerms}
                onChange={handleChange}
                onBlur={() => handleBlur("acceptTerms")}
                className={styles.termsCheckbox}
              />
              <span className={styles.termsText}>
                <a
                  href="/قوانین-و-مقررات"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  قوانین و شرایط
                </a>{" "}
                را می‌پذیرم *
              </span>
            </label>
            {touched.acceptTerms && errors.acceptTerms && (
              <span className={styles.errorText}>{errors.acceptTerms}</span>
            )}
          </div>

          {/* پیام‌های خطا و موفقیت */}
          {submitError && (
            <div className={styles.submitError}>
              <p>{submitError}</p>
            </div>
          )}

          {submitSuccess ? (
            <div className={styles.successMessage}>
              <p>سفارش شما با موفقیت ثبت شد!</p>
              <p>در حال انتقال به صفحه سفارش...</p>
            </div>
          ) : (
            <button
              type="submit"
              disabled={isSubmitting}
              className={styles.submitButton}
              aria-label="ثبت سفارش"
            >
              {isSubmitting ? (
                <>
                  <span className={styles.spinner}></span>
                  در حال ثبت...
                </>
              ) : (
                "ثبت سفارش"
              )}
            </button>
          )}
        </form>
      </div>

      <Footer />
    </>
  );
};

export default Register;
